import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Typography,
} from "@mui/material";
import HttpService from "../services/HttpService";
import OperationLogsTable from "./OperationLogsTable";

const buildLogsEndpoint = ({ isCustomer, customerId, entityType, entityId }) => {
    if (isCustomer) {
        return `customers/${customerId}/logs/orders/${entityId}`;
    }

    return `logs/orders/${entityId}`;
};

const OperationLogsDialog = ({
    open,
    onClose,
    entityType,
    entityId,
    title,
    isCustomer,
    customerId,
    limit = 200,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [response, setResponse] = useState({ orderId: null, logs: [] });

    const isValidRequest = useMemo(() => {
        if (!open) {
            return false;
        }

        if (!entityType || !entityId) {
            return false;
        }

        if (isCustomer && !customerId) {
            return false;
        }

        return true;
    }, [open, entityType, entityId, isCustomer, customerId]);

    useEffect(() => {
        if (!isValidRequest) {
            return;
        }

        setLoading(true);
        setError("");

        const endpoint = buildLogsEndpoint({ isCustomer, customerId, entityType, entityId });

        HttpService.getInstance()
            .getData(endpoint, "user-token", { limit })
            .then((apiResponse) => {
                setResponse({
                    orderId: apiResponse.data?.order_id || entityId,
                    logs: apiResponse.data?.logs || [],
                });
            })
            .catch(() => {
                setResponse({ orderId: null, logs: [] });
                setError("No se pudieron obtener los logs de operaciones.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [isValidRequest, isCustomer, customerId, entityType, entityId, limit]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>{title || "Log de operaciones"}</DialogTitle>

            <DialogContent dividers>
                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {!loading && error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {!loading && !error && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {`Orden: #${response.orderId || entityId}`}
                        </Typography>

                        <OperationLogsTable title="Logs" logs={response.logs} loading={loading} />
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary" variant="outlined">
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OperationLogsDialog;
